'use client';

import { useState, useMemo } from 'react';
import { ChevronDown, Grip } from 'lucide-react';
import useNavigation from '@/src/lib/hooks/useNavigation';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
  DrawerTitle,
  DrawerDescription,
} from '@/src/components/ui/drawer';
import { Button } from '@/src/components/ui/button';
import { IStoreDepartment } from '@/src/types/stores';
import { useLocale, useTranslations } from 'next-intl';
import Image from '@/src/components/Image';
import { useSearchParams } from 'next/navigation';

// الكومبوننت الآن يستقبل الداتا كـ Prop من السيرفر
export function CategoryFilter({
  fetchedCategories = [],
}: {
  fetchedCategories: IStoreDepartment[];
}) {
  const locale = useLocale();
  const isArabic = locale === 'ar';
  const t = useTranslations('stores');
  const { createQueryString, removeQueryString, isPending } = useNavigation();
  const searchParams = useSearchParams();

  const appliedSelected = useMemo(
    () =>
      new Set([...searchParams.getAll('departmentIds'), ...searchParams.getAll('departmentsIds')]),
    [searchParams],
  );

  const [selected, setSelected] = useState<Set<string>>(() => new Set(appliedSelected));

  const [open, setOpen] = useState(false);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setSelected(new Set(appliedSelected));
    }

    setOpen(nextOpen);
  };

  const isAllSelected = selected.size === 0;

  const handleCategoryToggle = (categoryId: string) => {
    if (categoryId === 'all') {
      setSelected(new Set());
      return;
    }

    const newSelected = new Set(selected);

    if (newSelected.has(categoryId)) {
      newSelected.delete(categoryId);
    } else {
      newSelected.add(categoryId);
    }

    setSelected(newSelected);
  };

  const handleApply = () => {
    const selectedArray = Array.from(selected);

    if (selectedArray.length > 0) {
      createQueryString({
        departmentIds: selectedArray,
        departmentsIds: [],
      });
    } else {
      removeQueryString(['departmentIds', 'departmentsIds']);
    }
    setOpen(false);
  };

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerTrigger asChild>
        <Button
          variant={appliedSelected.size > 0 ? 'default' : 'secondary'}
          size="sm"
          className="w-fit!"
        >
          {t('categories')} <ChevronDown className="ms-1 size-4" />
          {appliedSelected.size > 0 && (
            <span className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-black text-xs text-white">
              {appliedSelected.size}
            </span>
          )}
        </Button>
      </DrawerTrigger>

      <DrawerContent>
        <div className="mx-auto w-full max-w-3xl">
          <DrawerHeader>
            <DrawerTitle className="text-center text-2xl font-semibold text-gray-900">
              {t('storeCategories')}
            </DrawerTitle>
            <DrawerDescription className="sr-only">{t('storeCategories')}</DrawerDescription>
          </DrawerHeader>

          <div className="max-h-[60vh] overflow-y-auto px-8 pb-4">
            <div className="grid grid-cols-[repeat(auto-fill,minmax(72px,1fr))] justify-items-center gap-4">
              <button
                onClick={() => handleCategoryToggle('all')}
                aria-pressed={isAllSelected}
                className="group flex flex-col items-center gap-4 transition-all duration-200"
              >
                <div
                  className={`relative flex h-20 w-20 items-center justify-center rounded-full transition-all duration-200 ${
                    isAllSelected
                      ? 'border-4 border-gray-900 bg-white'
                      : 'border-4 border-transparent bg-gray-100 group-hover:border-gray-300'
                  }`}
                >
                  <Grip className="size-10" />
                </div>
                <p className="text-center text-sm leading-tight font-medium text-gray-900">
                  {t('all')}
                </p>
              </button>

              {fetchedCategories
                .filter((c) => c.id.toString() !== 'all')
                .map((category) => {
                  const isCurrentSelected = selected.has(category.id.toString());
                  const imageSrc =
                    (isArabic ? category.arabicImage : category.englishImage) ||
                    category.arabicImage ||
                    category.englishImage ||
                    '';
                  return (
                    <button
                      key={category.id}
                      onClick={() => handleCategoryToggle(category.id.toString())}
                      aria-pressed={isCurrentSelected}
                      className="group flex flex-col items-center gap-4 transition-all duration-200"
                    >
                      <div
                        className={`relative flex h-20 w-20 items-center justify-center rounded-full transition-all duration-200 ${
                          isCurrentSelected
                            ? 'border-4 border-gray-900 bg-white'
                            : 'border-4 border-transparent bg-gray-100 group-hover:border-gray-300'
                        }`}
                      >
                        <span className="relative h-full w-full overflow-hidden rounded-full p-2">
                          {imageSrc ? (
                            <Image
                              src={imageSrc}
                              alt={category.englishName}
                              fill
                              sizes="128px"
                              className="object-cover"
                            />
                          ) : (
                            <span className="flex h-full w-full items-center justify-center text-xl font-bold text-gray-500">
                              {category.englishName?.[0] || ''}
                            </span>
                          )}
                        </span>
                      </div>
                      <p className="px-2 text-center text-sm leading-tight font-medium text-gray-900">
                        {isArabic ? category.arabicName : category.englishName}
                      </p>
                    </button>
                  );
                })}
            </div>
          </div>

          <DrawerFooter>
            <button
              onClick={handleApply}
              disabled={isPending}
              className="w-full rounded-full bg-black py-4 text-lg font-semibold text-white transition-colors duration-200 hover:bg-gray-800 disabled:opacity-70"
            >
              {isPending ? t('applying') : t('apply')}
            </button>
            <DrawerClose asChild>
              <Button variant="ghost">{t('cancel')}</Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
